// Third-party
import { useMutation, useQuery } from '@tanstack/react-query';
import { Moment } from 'moment';
import { useSnackbar } from 'notistack';

// Redux-toolkit
import { useTranslation } from 'react-i18next';
import { getTimezone, getDatesRange } from '@mgm/common/utils';
import { usePostHog } from 'posthog-js/react';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { sheetsActions } from '../../store/sheets/slice';
import { getSelectedMember, getSelectedShift } from '../../store/sheets/selectors';
import { commonActions } from '../../store/common/slice';

// API
import { SheetsController } from '../../services';

import { useAppDispatch, useAppSelector } from '../useAppStore';
import { sortByOnlineMember } from '../../utils/helpers';
import { IBaseResponse, TAxiosError, TGetSheetsResponse } from '../../types/responses';
import { useSheets } from '../useSheets';
import { TTimesheetsList } from '../../types';
import { TAddShiftReq, TUpdateShiftReq } from '../../types/requests';
import { QUERY_KEYS } from '../../utils/enums';
import { useError } from '../useCommon';

type TGetSheets = {
  dates?: [Moment, Moment],
  isMainSheetsPage?: boolean
  byWeek?: boolean
  timezone?: string
};
export function useGetSheetsAPI(options?: TGetSheets) {
  const dispatch = useAppDispatch();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const selectedMember = useAppSelector(getSelectedMember);
  const { getSheetsByVisibility } = useSheets();
  const { handleReqError } = useError();

  const byWeek = options?.byWeek || false;

  const { fromDate, toDate } = getDatesRange(
    options?.dates,
    byWeek,
    false,
    options?.timezone || getTimezone(),
  );

  return useQuery(
    [`${QUERY_KEYS.TIMESHEETS}-${personnel?.email}`],
    async () => {
      const data = {
        to: toDate,
        from: fromDate,
        email: personnel?.email,
        shopName: personnel?.shopName,
        timezone: getTimezone(),
      };

      const rsp = await SheetsController().getSheets<TGetSheetsResponse>(data);

      if (rsp?.success && 'sheets' in rsp) {
        const sortedSheets = sortByOnlineMember(getSheetsByVisibility(rsp.sheets), personnel.email);
        dispatch(sheetsActions.setSheets(sortedSheets));
        dispatch(sheetsActions.setSearchedSheets(sortedSheets));

        if (selectedMember?.personnel?.email) {
          const currentMember = rsp.sheets.find(
            (sheet) => (
              sheet.personnel?.email
                    === selectedMember?.personnel?.email
            ),
          ) as TTimesheetsList;

          dispatch(sheetsActions.setSelectedMember(currentMember));
        }
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get sheets data failed! ${rsp.msg}`);
      }

      return rsp;
    },
    { staleTime: 3 * (60 * 1000), suspense: false, enabled: options?.isMainSheetsPage || false },
  );
}

export function useDeleteShiftAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const selectedShift = useAppSelector(getSelectedShift);
  const { enqueueSnackbar } = useSnackbar();
  const { refetch: refetchSheets } = useGetSheetsAPI();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('schedule');

  const { handleReqError } = useError();

  const deleteShiftApi = () => {
    const data = {
      shiftId: selectedShift.id,
      shopName: personnel.shopName,
    };

    return SheetsController().deleteShift<IBaseResponse>(data);
  };

  return useMutation(deleteShiftApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        await refetchSheets();
        dispatch(sheetsActions.setToggleDeleteShift(false));
        enqueueSnackbar(`${t('shiftWasDeleted')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! delete shift failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! delete shift request threw an Exception! ${error}`);
    },
  });
}

export function useAddShiftAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { refetch: refetchSheets } = useGetSheetsAPI();
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const { t } = useTranslation('schedule');
  const posthog = usePostHog();

  const addShiftApi = (extraData: Omit<TAddShiftReq['shiftData'], 'timezone'>) => {
    const data = {
      shiftData: {
        ...extraData,
        timezone: getTimezone(),
      },
      shopName: personnel.shopName,
    };

    return SheetsController().addShift<IBaseResponse>(data);
  };

  return useMutation(addShiftApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        await refetchSheets();
        dispatch(commonActions.setIsOpenPagesNav(false));

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          newMember: personnel?.email,
          addedAt: new Date().toISOString(),
          action: 'user_added_shift',
        });

        enqueueSnackbar(`${t('shiftWasAdded')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! add shift failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! add shift request threw an Exception! ${error}`);
    },
  });
}

export function useUpdateShiftAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { refetch: refetchSheets } = useGetSheetsAPI();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('schedule');
  const selectedShift = useAppSelector(getSelectedShift);
  const { handleReqError } = useError();

  const updateShiftApi = (extraData: Omit<TUpdateShiftReq['shiftData'], 'timezone'>) => {
    const data = {
      shiftData: {
        ...extraData,
        timezone: getTimezone(),
      },
      shopName: personnel.shopName,
      shiftId: selectedShift.id,
    };

    return SheetsController().updateShift<IBaseResponse>(data);
  };

  return useMutation(updateShiftApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        await refetchSheets();
        dispatch(commonActions.setIsOpenPagesNav(false));
        enqueueSnackbar(`${t('shiftWasUpdated')}`, { variant: 'success' });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! update shift failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! update shift request threw an Exception! ${error}`);
    },
  });
}
